import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useNavigate, useParams } from "react-router-dom";
import {
  getProjectProgressDetail,
  getProjectProgressProjects,
  getSectionWorkTypes,
  fixSectionPlan,
  unfixSectionPlan,
} from "../../../api/projectProgressApi";
import {
  clampPercent,
  formatDate,
  formatDateRange,
  formatDelayBadge,
  formatPercent,
  getDelayDays,
  getPersonDisplayName,
  getProjectLabel,
  getWorkTypeStatus,
  isWorkTypeOverdue,
  getSectionFactDateRange,
  splitSectionName,
} from "./projectsScheduleHelpers";
import AddWorkTypeModal from "./AddWorkTypeModal/AddWorkTypeModal";
import AddMilestoneModal from "./AddMilestoneModal/AddMilestoneModal";
import EditWorkTypeDrawer from "./EditWorkTypeDrawer/EditWorkTypeDrawer";
import DeleteWorkTypeModal from "./DeleteWorkTypeModal/DeleteWorkTypeModal";
import FixPlanErrorModal from "./FixPlanErrorModal/FixPlanErrorModal";
import "./ProjectsSchedule.css";
import "./ProjectsScheduleDetail.css";

const selectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: 44,
    borderRadius: 10,
    borderColor: "#d1d5db",
    boxShadow: "none",
    fontSize: 14,
    "&:hover": { borderColor: "#9ca3af" },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 20,
  }),
};

const EditIconButton = ({
  title = "Редактировать",
  disabled = false,
  onClick,
}) => (
  <button
    type="button"
    className="psd-edit-btn"
    title={title}
    disabled={disabled}
    onClick={onClick}
  >
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 20h4.5L19 9.5 14.5 5 4 15.5V20z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M12.5 7l4.5 4.5" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  </button>
);

const MetricCard = ({ title, contract, plan, fact, delayDays, children }) => {
  const delayLabel = formatDelayBadge(delayDays);

  return (
    <div className="psd-card">
      <div className="psd-card__title">{title}</div>
      {children || (
        <div className="psd-card__rows">
          <div className="psd-card__row">
            <span className="psd-card__key">По договору:</span>
            <span className="psd-card__val psd-card__val--contract">{contract}</span>
          </div>
          <div className="psd-card__row">
            <span className="psd-card__key">План:</span>
            <span className="psd-card__val psd-card__val--plan">{plan}</span>
          </div>
          <div className="psd-card__row">
            <span className="psd-card__key">Факт:</span>
            <span className="psd-card__val psd-card__val--fact">{fact}</span>
            {delayLabel && (
              <span
                className={`psd-delay${delayDays < 0 ? " psd-delay--early" : ""}`}
              >
                {delayLabel}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ProgressBar = ({ percent, className = "" }) => {
  const value = clampPercent(percent);
  return (
    <div className={`ps-progress ${className}`.trim()}>
      <div className="ps-progress__fill" style={{ width: `${value}%` }} />
      <span className="ps-progress__text">{Math.round(value)}%</span>
    </div>
  );
};

const SectionWorkTypesPanel = ({
  projectId,
  section,
  cache,
  onLoaded,
  onProjectRefresh,
}) => {
  const sectionId = section.id;
  const cached = cache[sectionId];
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addMilestoneOpen, setAddMilestoneOpen] = useState(false);
  const [editingWorkType, setEditingWorkType] = useState(null);
  const [deletingWorkType, setDeletingWorkType] = useState(null);
  const [fixingPlan, setFixingPlan] = useState(false);
  const [unfixingPlan, setUnfixingPlan] = useState(false);
  const [fixPlanError, setFixPlanError] = useState("");
  const [fixPlanErrorTitle, setFixPlanErrorTitle] = useState(
    "Нельзя зафиксировать план",
  );

  const isPlanFixed = Boolean(section.is_plan_fixed);

  useEffect(() => {
    if (cached) {
      setLoading(false);
      setError("");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError("");
        const data = await getSectionWorkTypes(projectId, sectionId);
        if (cancelled) return;
        onLoaded(sectionId, {
          work_types: Array.isArray(data?.work_types) ? data.work_types : [],
          unit_weight_sum: data?.unit_weight_sum ?? 0,
        });
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "Не удалось загрузить виды работ");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cached, projectId, sectionId, onLoaded]);

  const allItems = useMemo(() => {
    const list = Array.isArray(cached?.work_types) ? [...cached.work_types] : [];
    return list.sort(
      (a, b) => (a.order_index ?? 0) - (b.order_index ?? 0) || (a.id || 0) - (b.id || 0),
    );
  }, [cached]);

  const existingWorkTypeNames = useMemo(
    () => allItems.filter((wt) => !wt.is_milestone).map((wt) => wt.name).filter(Boolean),
    [allItems],
  );

  const existingMilestoneNames = useMemo(
    () => allItems.filter((wt) => wt.is_milestone).map((wt) => wt.name).filter(Boolean),
    [allItems],
  );

  const refreshSectionAndProject = useCallback(async () => {
    const data = await getSectionWorkTypes(projectId, sectionId);
    onLoaded(sectionId, {
      work_types: Array.isArray(data?.work_types) ? data.work_types : [],
      unit_weight_sum: data?.unit_weight_sum ?? 0,
    });
    await onProjectRefresh?.();
  }, [projectId, sectionId, onLoaded, onProjectRefresh]);

  const handleCreated = async () => {
    try {
      await refreshSectionAndProject();
    } catch (e) {
      setError(e?.message || "Не удалось обновить виды работ");
    }
  };

  const handleSaved = async () => {
    setEditingWorkType(null);
    try {
      await refreshSectionAndProject();
    } catch (e) {
      setError(e?.message || "Не удалось обновить виды работ");
    }
  };

  const handleDeleted = async () => {
    setDeletingWorkType(null);
    setEditingWorkType(null);
    try {
      await refreshSectionAndProject();
    } catch (e) {
      setError(e?.message || "Не удалось обновить виды работ");
    }
  };

  const handleFixPlan = async () => {
    if (isPlanFixed || fixingPlan || unfixingPlan) return;
    try {
      setFixingPlan(true);
      setFixPlanError("");
      setFixPlanErrorTitle("Нельзя зафиксировать план");
      await fixSectionPlan(projectId, sectionId);
      await refreshSectionAndProject();
    } catch (e) {
      setFixPlanErrorTitle("Нельзя зафиксировать план");
      setFixPlanError(e?.message || "Не удалось зафиксировать план");
    } finally {
      setFixingPlan(false);
    }
  };

  const handleUnfixPlan = async () => {
    if (!isPlanFixed || fixingPlan || unfixingPlan) return;
    try {
      setUnfixingPlan(true);
      setFixPlanError("");
      setFixPlanErrorTitle("Нельзя отменить фиксацию");
      await unfixSectionPlan(projectId, sectionId);
      await refreshSectionAndProject();
    } catch (e) {
      setFixPlanErrorTitle("Нельзя отменить фиксацию");
      setFixPlanError(e?.message || "Не удалось отменить фиксацию плана");
    } finally {
      setUnfixingPlan(false);
    }
  };

  const sectionLabel = useMemo(() => {
    const { code, title } = splitSectionName(section.name);
    return title ? `${code} ${title}` : code;
  }, [section.name]);

  return (
    <div className="psd-expand-panel">
      <div className="psd-expand-panel__header">
        <h3 className="psd-expand-panel__title">Виды работ и вехи</h3>
        <div className="psd-expand-panel__actions">
          <button
            type="button"
            className="psd-add-btn"
            onClick={() => setAddModalOpen(true)}
            disabled={isPlanFixed}
            title={isPlanFixed ? "План зафиксирован — добавление недоступно" : undefined}
          >
            + Работа
          </button>
          <button
            type="button"
            className="psd-add-btn psd-add-btn--milestone"
            onClick={() => setAddMilestoneOpen(true)}
            disabled={isPlanFixed}
            title={isPlanFixed ? "План зафиксирован — добавление недоступно" : undefined}
          >
            ◆ Веха
          </button>
        </div>
      </div>

      {isPlanFixed && (
        <div className="psd-fixed-banner">
          <span aria-hidden>🔒</span>
          План раздела зафиксирован. Плановые сроки и веса недоступны для
          изменения.
        </div>
      )}

      {loading ? (
        <div className="psd-expand-panel__state">Загрузка видов работ...</div>
      ) : error ? (
        <div className="psd-expand-panel__state psd-expand-panel__state--error">
          {error}
        </div>
      ) : (
        <>
        <div className="psd-nested-table-wrap">
          <table className="psd-nested-table">
            <thead>
              <tr>
                <th>Наименование</th>
                <th>Тип</th>
                <th>Статус</th>
                <th>Удельный вес</th>
                <th>Плановый срок</th>
                <th>План готовности</th>
                <th>Фактический срок</th>
                <th>% Выполнения</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {allItems.length === 0 ? (
                <tr>
                  <td colSpan={9} className="psd-expand-panel__state">
                    Виды работ и вехи не найдены
                  </td>
                </tr>
              ) : (
                allItems.map((wt) => {
                  const isMilestone = Boolean(wt.is_milestone);
                  const status = getWorkTypeStatus(wt);
                  const overdue = isWorkTypeOverdue(wt);
                  const rowCls = [
                    "psd-wt-row--clickable",
                    isMilestone ? "psd-row--milestone" : "",
                    `psd-row--${status}`,
                    overdue ? "psd-row--overdue" : "",
                    editingWorkType?.id === wt.id ? "psd-wt-row--active" : "",
                  ].filter(Boolean).join(" ") || undefined;

                  return (
                    <tr
                      key={wt.id}
                      className={rowCls}
                      onClick={() => setEditingWorkType(wt)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setEditingWorkType(wt);
                        }
                      }}
                    >
                      <td>
                        <div className="psd-wt-name">
                          {isMilestone && (
                            <span className="psd-milestone-icon" title="Веха" aria-label="Веха">◆</span>
                          )}
                          {wt.name || "—"}
                        </div>
                      </td>
                      <td>
                        <span className={`psd-type-badge${isMilestone ? " psd-type-badge--milestone" : ""}`}>
                          {isMilestone ? "Веха" : "Работа"}
                        </span>
                      </td>
                      <td>
                        <span className={`psd-status-badge psd-status-badge--${status}`}>
                          {status === "completed" && "✓ Выполнено"}
                          {status === "in_progress" && "● В работе"}
                          {status === "pending" && "○ Ожидает"}
                        </span>
                      </td>
                      <td>{isMilestone ? "—" : formatPercent(wt.unit_weight_percent, 0)}</td>
                      <td className="psd-plan-range">
                        {isMilestone
                          ? formatDate(wt.plan_start_date || wt.plan_end_date)
                          : formatDateRange(wt.plan_start_date, wt.plan_end_date)}
                      </td>
                      <td>{isMilestone ? "—" : formatPercent(wt.readiness_plan_today_percent, 0)}</td>
                      <td className="psd-plan-range">
                        {isMilestone
                          ? formatDate(wt.fact_start_date || wt.fact_end_date)
                          : formatDateRange(wt.fact_start_date, wt.fact_end_date)}
                      </td>
                      <td>{isMilestone ? "—" : formatPercent(wt.completion_percent, 0)}</td>
                      <td>
                        <EditIconButton
                          title={isMilestone ? "Редактировать веху" : "Редактировать вид работ"}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingWorkType(wt);
                          }}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {allItems.some((wt) => !wt.is_milestone) && (
              <tfoot>
                <tr>
                  <td colSpan={3}>
                    <strong>Сумма удельных весов</strong>
                  </td>
                  <td>
                    <strong>{formatPercent(cached?.unit_weight_sum ?? 0, 0)}</strong>
                  </td>
                  <td colSpan={5} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        </>
      )}

      <div className="psd-expand-panel__footer">
        {!isPlanFixed ? (
          <button
            type="button"
            className="psd-fix-plan-btn"
            onClick={handleFixPlan}
            disabled={fixingPlan || unfixingPlan}
          >
            {fixingPlan ? "Фиксация..." : "Зафиксировать план"}
          </button>
        ) : (
          <div className="psd-fixed-footer">
            <div className="psd-fixed-banner psd-fixed-banner--footer">
              <span aria-hidden>🔒</span>
              План раздела зафиксирован. Плановые сроки и веса недоступны для
              изменения. Отменить фиксацию может только инициатор.
            </div>
            <button
              type="button"
              className="psd-unfix-plan-btn"
              onClick={handleUnfixPlan}
              disabled={unfixingPlan || fixingPlan}
              title="Отменить может только инициатор фиксации"
            >
              {unfixingPlan ? "Отмена фиксации..." : "Отменить фиксацию"}
            </button>
          </div>
        )}
      </div>

      <AddWorkTypeModal
        open={addModalOpen}
        projectId={projectId}
        sectionId={sectionId}
        existingNames={existingWorkTypeNames}
        onClose={() => setAddModalOpen(false)}
        onCreated={handleCreated}
      />

      <AddMilestoneModal
        open={addMilestoneOpen}
        projectId={projectId}
        sectionId={sectionId}
        existingNames={existingMilestoneNames}
        onClose={() => setAddMilestoneOpen(false)}
        onCreated={handleCreated}
      />

      <EditWorkTypeDrawer
        open={Boolean(editingWorkType)}
        projectId={projectId}
        section={section}
        workType={editingWorkType}
        siblingWorkTypes={allItems}
        planFixed={isPlanFixed}
        onClose={() => setEditingWorkType(null)}
        onSaved={handleSaved}
        onRequestDelete={(wt) => setDeletingWorkType(wt)}
      />

      <DeleteWorkTypeModal
        open={Boolean(deletingWorkType)}
        projectId={projectId}
        workType={deletingWorkType}
        sectionName={sectionLabel}
        onClose={() => setDeletingWorkType(null)}
        onDeleted={handleDeleted}
      />

      <FixPlanErrorModal
        open={Boolean(fixPlanError)}
        sectionName={section.name}
        title={fixPlanErrorTitle}
        message={fixPlanError}
        onClose={() => setFixPlanError("")}
      />
    </div>
  );
};

const ProjectsScheduleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const projectId = Number(id);

  const [viewMode, setViewMode] = useState("table");
  const [project, setProject] = useState(null);
  const [projectOptions, setProjectOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [workTypesCache, setWorkTypesCache] = useState({});

  const loadDetail = useCallback(async (pid) => {
    try {
      setLoading(true);
      setError("");
      setExpandedIds(new Set());
      setWorkTypesCache({});
      const data = await getProjectProgressDetail(pid, {
        include_work_types: false,
      });
      setProject(data);
    } catch (e) {
      setProject(null);
      setError(e?.message || "Не удалось загрузить график проекта");
    } finally {
      setLoading(false);
    }
  }, []);

  // Тихое обновление проекта без сворачивания разделов
  const refreshProject = useCallback(async () => {
    if (!projectId) return;
    try {
      const data = await getProjectProgressDetail(projectId, {
        include_work_types: false,
      });
      setProject(data);
    } catch (e) {
      console.error("Не удалось обновить график проекта:", e);
    }
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    loadDetail(projectId);
  }, [projectId, loadDetail]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getProjectProgressProjects({ exclude: "ЭП" });
        const list = Array.isArray(data?.project_portfolio)
          ? data.project_portfolio
          : Array.isArray(data)
            ? data
            : [];
        if (cancelled) return;
        setProjectOptions(
          list.map((p) => ({
            value: p.id,
            label: getProjectLabel(p),
          })),
        );
      } catch {
        // селект не критичен
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleWorkTypesLoaded = useCallback((sectionId, payload) => {
    setWorkTypesCache((prev) => ({
      ...prev,
      [sectionId]: payload,
    }));
  }, []);

  const selectedOption = useMemo(() => {
    if (!project) return null;
    const fromList = projectOptions.find((o) => o.value === project.id);
    return fromList || { value: project.id, label: getProjectLabel(project) };
  }, [project, projectOptions]);

  const sections = useMemo(() => {
    const list = Array.isArray(project?.sections) ? [...project.sections] : [];
    return list.sort(
      (a, b) =>
        (a.order_index ?? 0) - (b.order_index ?? 0) || (a.id || 0) - (b.id || 0),
    );
  }, [project]);

  const toggleExpand = (sectionId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const startDelay = getDelayDays(project?.start_date?.fact, project?.start_date?.plan);
  const endDelay = getDelayDays(project?.end_date?.fact, project?.end_date?.plan);
  const completion = clampPercent(project?.completion_percent);

  return (
    <div className="ps-page psd-page">
      <div className="psd-header">
        <button
          type="button"
          className="psd-back"
          onClick={() => navigate("/projects/schedule")}
          aria-label="Назад к списку"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M15 18l-6-6 6-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h1 className="psd-title">График проекта</h1>
        <div className="psd-project-select">
          <Select
            styles={selectStyles}
            options={projectOptions}
            value={selectedOption}
            onChange={(opt) => {
              if (opt?.value) navigate(`/projects/schedule/${opt.value}`);
            }}
            placeholder="Выберите проект..."
            isSearchable
            noOptionsMessage={() => "Нет проектов"}
          />
        </div>
      </div>

      {loading ? (
        <div className="ps-state">Загрузка...</div>
      ) : error ? (
        <div className="ps-state ps-state--error">{error}</div>
      ) : !project ? (
        <div className="ps-state">Проект не найден</div>
      ) : (
        <>
          <div className="psd-metrics">
            <MetricCard
              title="Дата начала"
              contract={formatDate(project.start_date?.contract)}
              plan={formatDate(project.start_date?.plan)}
              fact={formatDate(project.start_date?.fact)}
              delayDays={startDelay}
            />
            <MetricCard
              title="Дата окончания"
              contract={formatDate(project.end_date?.contract)}
              plan={formatDate(project.end_date?.plan)}
              fact={formatDate(project.end_date?.fact)}
              delayDays={endDelay}
            />
            <MetricCard
              title="Длительность"
              contract={`${project.duration_days?.contract ?? 0} дн.`}
              plan={`${project.duration_days?.plan ?? 0} дн.`}
              fact={`${project.duration_days?.fact ?? 0} дн.`}
            />
            <div className="psd-card psd-card--completion">
              <div className="psd-card__title-row">
                <span className="psd-card__title">% Завершения</span>
                <span className="psd-card__completion-value">
                  {Math.round(completion)}%
                </span>
              </div>
              <ProgressBar percent={completion} className="psd-completion-bar" />
            </div>
          </div>

          <div className="psd-view-toggle">
            <button
              type="button"
              className={`psd-view-btn${viewMode === "chart" ? " psd-view-btn--active" : ""}`}
              onClick={() => setViewMode("chart")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M4 19V5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M4 19h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M8 15v-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M12 15V8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M16 15v-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
              График
            </button>
            <button
              type="button"
              className={`psd-view-btn${viewMode === "table" ? " psd-view-btn--active" : ""}`}
              onClick={() => setViewMode("table")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M4 7h16M4 12h16M4 17h16"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
              Таблица
            </button>
          </div>

          {viewMode === "chart" ? (
            <div className="ps-table-card">
              <div className="ps-state">Представление «График» в разработке</div>
            </div>
          ) : (
            <div className="ps-table-card psd-sections-card">
              <div className="ps-table-wrap">
                <table className="ps-table psd-sections-table">
                  <thead>
                    <tr>
                      <th>Разделы проекта</th>
                      <th>Ответственный</th>
                      <th>Плановый срок</th>
                      <th>Фактический срок</th>
                      <th>% Выполнения</th>
                      <th>Доля раздела</th>
                      <th>% Вклад в РП</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sections.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="ps-state">
                          Разделы не найдены
                        </td>
                      </tr>
                    ) : (
                      sections.map((section) => {
                        const { code, title } = splitSectionName(section.name);
                        const isExpanded = expandedIds.has(section.id);
                        const factRange = getSectionFactDateRange(
                          section,
                          workTypesCache[section.id]?.work_types,
                        );

                        return (
                          <React.Fragment key={section.id}>
                            <tr
                              className={`psd-section-row${
                                isExpanded ? " psd-section-row--expanded" : ""
                              }`}
                            >
                              <td>
                                <div className="psd-section-name">
                                  <span className="psd-drag" aria-hidden>
                                    ⋮⋮
                                  </span>
                                  <button
                                    type="button"
                                    className="psd-expand"
                                    onClick={() => toggleExpand(section.id)}
                                    aria-expanded={isExpanded}
                                    aria-label={
                                      isExpanded ? "Свернуть" : "Развернуть"
                                    }
                                  >
                                    <svg
                                      width="14"
                                      height="14"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      className={
                                        isExpanded ? "psd-expand__icon--open" : ""
                                      }
                                    >
                                      <path
                                        d="M9 6l6 6-6 6"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </button>
                                  <div className="psd-section-name__text">
                                    <div className="psd-section-name__code">
                                      {code}
                                      {section.is_plan_fixed && (
                                        <span
                                          className="psd-lock"
                                          title="План зафиксирован"
                                          aria-label="План зафиксирован"
                                        >
                                          <svg
                                            width="12"
                                            height="12"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                          >
                                            <rect
                                              x="5"
                                              y="11"
                                              width="14"
                                              height="10"
                                              rx="2"
                                              stroke="currentColor"
                                              strokeWidth="1.8"
                                            />
                                            <path
                                              d="M8 11V8a4 4 0 1 1 8 0v3"
                                              stroke="currentColor"
                                              strokeWidth="1.8"
                                              strokeLinecap="round"
                                            />
                                          </svg>
                                        </span>
                                      )}
                                    </div>
                                    {title && (
                                      <div className="psd-section-name__title">
                                        {title}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td>{getPersonDisplayName(section.responsible)}</td>
                              <td className="psd-plan-range">
                                {formatDateRange(
                                  section.plan_start_date,
                                  section.plan_end_date,
                                )}
                              </td>
                              <td className="psd-plan-range">
                                {formatDateRange(factRange.start, factRange.end)}
                              </td>
                              <td>
                                <ProgressBar
                                  percent={section.completion_percent}
                                  className="psd-section-progress"
                                />
                              </td>
                              <td>{formatPercent(section.share_percent)}</td>
                              <td>
                                {formatPercent(section.contribution_percent)}
                              </td>
                              <td>
                                <EditIconButton
                                  title="Редактирование раздела скоро"
                                  disabled
                                />
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr className="psd-expand-row">
                                <td colSpan={8}>
                                  <SectionWorkTypesPanel
                                    projectId={projectId}
                                    section={section}
                                    cache={workTypesCache}
                                    onLoaded={handleWorkTypesLoaded}
                                    onProjectRefresh={refreshProject}
                                  />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                  {sections.length > 0 && (
                    <tfoot>
                      <tr className="psd-total-row">
                        <td>
                          <strong>ИТОГО по РП</strong>
                        </td>
                        <td>{getPersonDisplayName(project.chief)}</td>
                        <td className="psd-plan-range">
                          {formatDateRange(
                            project.start_date?.plan,
                            project.end_date?.plan,
                          )}
                        </td>
                        <td className="psd-plan-range">
                          {formatDateRange(
                            project.start_date?.fact,
                            project.end_date?.fact,
                          )}
                        </td>
                        <td>
                          <ProgressBar
                            percent={completion}
                            className="psd-section-progress"
                          />
                        </td>
                        <td>
                          <strong>
                            {formatPercent(project.sections_share_sum ?? 100, 0)}
                          </strong>
                        </td>
                        <td />
                        <td />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProjectsScheduleDetail;
